import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  selectScreenName,
  selectStripeAccountId,
  selectStripeAccountName,
} from 'root-redux/selects/common'
import { selectPrimerProcessorName } from 'root-redux/selects/user'

import { createProductId } from 'helpers/createProductId'
import { sendAddToCartEvents } from 'helpers/sendAddToCartEvents'

import { PaymentMethod, PaymentSystem } from 'modules/purchase/constants'
import {
  selectCurrency,
  selectSubscriptionFullPrice,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialPeriodPrice,
} from 'modules/purchase/redux/selects/common'

import { eventLogger } from 'services/eventLogger.service'

export const usePrimerAnalytics = (): {
  logPaymentStarted: (paymentMethod: PaymentMethod) => void
  logPaypalPaymentStarted: () => void
  logPaymentFailed: ({
    error,
    paymentMethod,
  }: {
    error: any
    paymentMethod: PaymentMethod
  }) => void
} => {
  const currentPrice = useSelector(selectSubscriptionFullPrice)
  const trialPeriodPrice = useSelector(selectSubscriptionTrialPeriodPrice)
  const currency = useSelector(selectCurrency)
  const screenName = useSelector(selectScreenName)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const stripeAccountId = useSelector(selectStripeAccountId)
  const periodQuantity = useSelector(selectSubscriptionPeriodQuantity)
  const periodName = useSelector(selectSubscriptionPeriodName)
  const processorName = useSelector(selectPrimerProcessorName)

  const productId = useMemo(
    () =>
      createProductId({
        periodName,
        periodQuantity,
        price: currentPrice,
      }),
    [periodName, periodQuantity, currentPrice],
  )

  const logPaymentStarted = useCallback(
    (paymentMethod) => {
      eventLogger.logPurchaseStarted({
        screenName,
        productId,
        priceDetails: {
          price: currentPrice,
          trial: !!trialPeriodPrice,
          currency,
        },
        paymentMethod,
        paymentSystem: PaymentSystem.PRIMER,
        stripeAccountName,
        stripeAccountId,
        processorName,
      })

      sendAddToCartEvents()
    },
    [
      currency,
      currentPrice,
      productId,
      screenName,
      stripeAccountId,
      stripeAccountName,
      trialPeriodPrice,
      processorName,
    ],
  )

  const logPaymentFailed = useCallback(
    ({ error, paymentMethod }) => {
      eventLogger.logPurchaseFailed({
        productId,
        priceDetails: {
          price: currentPrice,
          trial: !!trialPeriodPrice,
          currency,
        },
        error: {
          type: error.type,
          code: error.code,
          description: error.message,
        },
        paymentMethod,
        screenName,
        paymentSystem: PaymentSystem.PRIMER,
        stripeAccountId,
        stripeAccountName,
        processorName,
      })
    },
    [
      currency,
      currentPrice,
      productId,
      screenName,
      stripeAccountName,
      stripeAccountId,
      trialPeriodPrice,
      processorName,
    ],
  )

  const logPaypalPaymentStarted = useCallback(() => {
    eventLogger.logPurchaseStartedPayPal({
      screenName,
      productId,
      priceDetails: {
        price: currentPrice,
        trial: !!trialPeriodPrice,
        currency,
      },
      paymentMethod: PaymentMethod.PAYPAL,
      paymentSystem: PaymentSystem.PRIMER,
      accountName: stripeAccountName,
      accountId: stripeAccountId,
    })
  }, [
    screenName,
    productId,
    currentPrice,
    trialPeriodPrice,
    currency,
    stripeAccountName,
    stripeAccountId,
  ])

  return {
    logPaymentStarted,
    logPaymentFailed,
    logPaypalPaymentStarted,
  }
}
